import { useCallback, useState } from 'react';

export type ThemeId = 'violet' | 'nature' | 'light' | 'cobalt';

const STORAGE_KEY = 'canebase_theme';
const THEME_IDS: ThemeId[] = ['violet', 'nature', 'light', 'cobalt'];

/** Farbe der Kopfzeile je Theme fuer die Browser-/PWA-Leiste (<meta name="theme-color">) - in den Dunkel-Themes
 * der Seitenhintergrund (dark-900), im Hell-Theme die weisse Kopfzeile (dark-950). */
const THEME_BAR_COLORS: Record<ThemeId, string> = {
  violet: '#15132a',
  nature: '#15132a',
  light: '#ffffff',
  cobalt: '#071525'
};

export interface ThemeDefinition {
  id: ThemeId;
  /** Drei Vorschau-Farben (primary/secondary/accent) als feste Hex-Werte fuer die Auswahlkarte -
   * bewusst NICHT ueber CSS-Variablen, da die Karte des jeweils anderen Themes sonst in den
   * Farben des gerade aktiven Themes gerendert wuerde. */
  preview: [string, string, string];
}

/** Label/Beschreibung liegen als Uebersetzungs-Keys in account.json (design.themes.<id>.*),
 * nicht hier - siehe Account.tsx. */
export const THEMES: ThemeDefinition[] = [
  { id: 'violet', preview: ['#6366f1', '#06b6d4', '#f43f5e'] },
  { id: 'nature', preview: ['#10b981', '#14b8a6', '#f97316'] },
  { id: 'light', preview: ['#2563eb', '#0d736f', '#d97706'] },
  { id: 'cobalt', preview: ['#087CC1', '#08BCEB', '#F2B84B'] }
];

export function getStoredTheme(): ThemeId {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return (THEME_IDS as string[]).includes(stored ?? '') ? (stored as ThemeId) : 'violet';
  } catch {
    // Kein localStorage verfuegbar (z.B. privater Modus) - Standard-Design verwenden.
    return 'violet';
  }
}

/** Setzt das Theme auf <html> und merkt es sich. Wird auch beim Start (main.tsx) aufgerufen,
 * bevor React ueberhaupt rendert, damit keine Farb-Umschaltung sichtbar aufblitzt. */
export function applyTheme(theme: ThemeId) {
  if (theme === 'violet') {
    document.documentElement.removeAttribute('data-theme');
  } else {
    document.documentElement.setAttribute('data-theme', theme);
  }
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_BAR_COLORS[theme]);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Auswahl gilt dann nur fuer diese Sitzung.
  }
}

export function useTheme() {
  const [theme, setThemeState] = useState<ThemeId>(getStoredTheme);

  const setTheme = useCallback((next: ThemeId) => {
    applyTheme(next);
    setThemeState(next);
  }, []);

  return { theme, setTheme };
}
